import { z } from 'zod';

// ---------------------------------------------------------------------------
// NotificationType enum (mirrors Prisma enum)
// ---------------------------------------------------------------------------

export const NOTIFICATION_TYPES = [
  'TICKET_ASSIGNED',
  'TICKET_TRANSITIONED',
  'TICKET_APPROVED',
  'TICKET_REJECTED',
  'APPROVAL_REQUESTED',
  'BATCH_READY',
  'SYSTEM_ALERT',
  'EXCEL_UPLOAD_COMPLETE',
  'EXCEL_UPLOAD_FAILED',
  'CLOSURE_COMPLETE',
  'PACKAGE_READY',
  'LOTE_SENT',
  'REMINDER_FIRMA',
  'RESSO_THRESHOLD_BREACH',
  'RESSO_DOCUMENT_EXPIRING',
] as const;

export const NotificationTypeSchema = z.enum(NOTIFICATION_TYPES);
export type NotificationTypeValue = z.infer<typeof NotificationTypeSchema>;

// ---------------------------------------------------------------------------
// Notification list query
// ---------------------------------------------------------------------------

export const NotificationListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export type NotificationListQuery = z.infer<typeof NotificationListQuerySchema>;

// ---------------------------------------------------------------------------
// Notification response (from API)
// ---------------------------------------------------------------------------

export const NotificationResponseSchema = z.object({
  id: z.number(),
  type: NotificationTypeSchema,
  title: z.string(),
  message: z.string(),
  read: z.boolean(),
  metadata: z.record(z.unknown()).default({}),
  createdAt: z.string(),
});

export type NotificationResponse = z.infer<typeof NotificationResponseSchema>;

// ---------------------------------------------------------------------------
// Channel preference per event type
// ---------------------------------------------------------------------------

export const ChannelPreferenceItemSchema = z.object({
  eventType: NotificationTypeSchema,
  emailEnabled: z.boolean(),
  pushEnabled: z.boolean(),
  inAppEnabled: z.boolean(),
});

export type ChannelPreferenceItem = z.infer<typeof ChannelPreferenceItemSchema>;

// ---------------------------------------------------------------------------
// Notification preference update
// ---------------------------------------------------------------------------

export const NotificationPreferenceUpdateSchema = z.object({
  channelPreferences: z.array(ChannelPreferenceItemSchema),
  quietHoursStart: z.string().nullable().optional(),
  quietHoursEnd: z.string().nullable().optional(),
});

export type NotificationPreferenceUpdate = z.infer<typeof NotificationPreferenceUpdateSchema>;

// ---------------------------------------------------------------------------
// Notification preference response
// ---------------------------------------------------------------------------

export const NotificationPreferenceResponseSchema = z.object({
  emailEnabled: z.boolean(),
  pushEnabled: z.boolean(),
  quietHoursStart: z.string().nullable(),
  quietHoursEnd: z.string().nullable(),
  channelPreferences: z.array(ChannelPreferenceItemSchema),
});

export type NotificationPreferenceResponse = z.infer<typeof NotificationPreferenceResponseSchema>;

// ---------------------------------------------------------------------------
// Push subscription
// ---------------------------------------------------------------------------

export const PushSubscriptionSchema = z.object({
  endpoint: z.string().url(),
  keys: z.object({
    p256dh: z.string().min(1),
    auth: z.string().min(1),
  }),
  userAgent: z.string().optional(),
});

export type PushSubscriptionInput = z.infer<typeof PushSubscriptionSchema>;

// ---------------------------------------------------------------------------
// Push subscription delete
// ---------------------------------------------------------------------------

export const PushSubscriptionDeleteSchema = z.object({
  endpoint: z.string().url(),
});

export type PushSubscriptionDeleteInput = z.infer<typeof PushSubscriptionDeleteSchema>;

// ---------------------------------------------------------------------------
// Unread count response
// ---------------------------------------------------------------------------

export const UnreadCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});

export type UnreadCountResponse = z.infer<typeof UnreadCountResponseSchema>;
